import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useReducerAsync } from 'hooks/useReducerAsync'
import { asyncActionHandlers, initialState, reducer } from './rangeLocationsReducer'
import { useTargeting } from '../TargetingContext'
import { withCampaignWideTargetingFallback } from '../targeting'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'

export const RangeLocationsContext = React.createContext()
export const RangeLocationsDispatchContext = React.createContext()

export const useRangeLocations = () => {
  const context = React.useContext(RangeLocationsContext)
  if (context === undefined) {
    throw new Error('useRangeLocations must be used within a RangeLocationsContextProvider')
  }
  return context
}

export const useRangeLocationsDispatch = () => {
  const context = React.useContext(RangeLocationsDispatchContext)
  if (context === undefined) {
    throw new Error('useRangeLocationsDispatch must be used within a RangeLocationsContextProvider')
  }
  return context
}

export default function RangeLocationsContextProvider({ tc, children, isFirstTc = false }) {
  const { value: targeting } = useTargeting()
  const { setup } = useCampaignSetup()
  const tcTargeting = useMemo(() => targeting.find((x) => x.tc === tc) ?? { tc }, [targeting, tc])
  // See PostalCodes.jsx's identical fallback for why: a gated non-first TC's
  // own entry has that platform's GEO_TARGETING_FIELDS stripped once
  // campaign_targeting is the geo source (audience/age stay untouched).
  const effectiveTcTargeting = useMemo(
    () => withCampaignWideTargetingFallback(tcTargeting, setup, isFirstTc),
    [tcTargeting, setup, isFirstTc],
  )
  const [state, dispatch] = useReducerAsync(reducer, initialState(effectiveTcTargeting), asyncActionHandlers)

  return (
    <RangeLocationsContext.Provider value={state}>
      <RangeLocationsDispatchContext.Provider value={dispatch}>{children}</RangeLocationsDispatchContext.Provider>
    </RangeLocationsContext.Provider>
  )
}

RangeLocationsContextProvider.propTypes = {
  tc: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isFirstTc: PropTypes.bool,
}
